import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {object} NameEmbroideryRefs
 * @property {HTMLInputElement} textInput - The name input.
 * @property {HTMLElement} characterCount - The character counter.
 * @property {HTMLElement} [preview] - The preview card. Absent when the preview is turned off.
 * @property {HTMLElement} [previewName] - The rendered name inside the preview card.
 * @property {HTMLElement} [previewIcon] - Slot for the chosen stitched icon.
 */

/**
 * Personalization field for the embroidered name, with a live preview that
 * follows the selected yarn color.
 *
 * @extends Component<NameEmbroideryRefs>
 */
class NameEmbroidery extends Component {
  requiredRefs = ['textInput', 'characterCount'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {Map<string, string>} */
  #colorMap = new Map();

  /** @type {HTMLElement | null} */
  #root = null;

  /** @type {string} */
  #placeholderName = '';

  /** @type {string[]} Yarn colors in play; more than one means multicolor. */
  #colors = [];

  /** @type {string} The block's letter-case setting, applied in JS when
   * rendering per-letter spans. */
  #previewCase = 'none';

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    this.#root = this.querySelector('.name-embroidery');
    this.#colorMap = this.#parseColorMap();
    this.#placeholderName = this.refs.previewName?.textContent?.trim() ?? '';
    this.#previewCase =
      this.#root?.style.getPropertyValue('--ke-preview-case').trim() || 'none';

    // The variant picker announces selections on the section element.
    this.closest('.shopify-section')?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    // Yarn color lives in a line item property picker, not a variant, so it
    // announces itself on document. Literal name avoids an importmap entry.
    document.addEventListener('knittykids:yarn-color', this.#handleYarnColor, { signal });
    document.addEventListener('knittykids:icon', this.#handleIcon, { signal });

    // A required-but-empty field blocks submission from the sticky bar, where
    // the input is off screen. Bring it into view so the block isn't silent.
    this.refs.textInput.addEventListener('invalid', this.#handleInvalid, { signal });

    // Pick up the color the page rendered with, and any value the browser restored.
    this.#syncColorFromDom();
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  handleInput() {
    this.#render();
  }

  /**
   * Updates the character counter and the preview to match the current value.
   */
  #render() {
    const { textInput, characterCount, preview, previewName } = this.refs;
    const value = textInput.value.trim();

    const template = characterCount.getAttribute('data-template');
    if (template) {
      characterCount.textContent = template
        .replace('[current]', textInput.value.length.toString())
        .replace('[max]', textInput.maxLength.toString());
    }

    if (!preview || !previewName) return;

    const isEmpty = value === '';
    preview.dataset.empty = isEmpty ? 'true' : 'false';
    const shown = isEmpty ? this.#placeholderName : value;

    // A multicolor yarn stitches each letter in a different thread, so the
    // preview cycles the palette the same way the basket does.
    if (!isEmpty && this.#colors.length > 1) {
      // One span per letter makes every letter a word start for CSS
      // text-transform, so the case is applied here instead.
      previewName.style.textTransform = 'none';
      previewName.textContent = '';
      let letterIndex = 0;
      for (const character of this.#applyCase(shown)) {
        const span = document.createElement('span');
        span.textContent = character;
        if (character.trim() !== '') {
          span.style.color = this.#colors[letterIndex % this.#colors.length];
          letterIndex++;
        }
        previewName.appendChild(span);
      }
      return;
    }

    previewName.style.removeProperty('text-transform');
    previewName.textContent = shown;
  }

  /**
   * Mirrors the CSS text-transform of the block's letter-case setting.
   * @param {string} value
   * @returns {string}
   */
  #applyCase(value) {
    if (this.#previewCase === 'uppercase') return value.toUpperCase();
    if (this.#previewCase === 'capitalize') {
      return value.replace(/(^|\s)(\p{L})/gu, (_, lead, letter) => lead + letter.toUpperCase());
    }
    return value;
  }

  /**
   * Reads the `Valore=#HEX` lines from the block setting.
   * @returns {Map<string, string>}
   */
  #parseColorMap() {
    const map = new Map();
    const raw = this.#root?.getAttribute('data-color-map');
    if (!raw) return map;

    for (const line of raw.split('\n')) {
      const [name, color] = line.split('=');
      if (!name || !color) continue;
      map.set(name.trim().toLowerCase(), color.trim());
    }

    return map;
  }

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    // Product cards (quick add, recommendations) fire the same event.
    if (event.target instanceof Element && event.target.closest('product-card')) return;

    const optionName = this.#optionName;
    const selected = event.selectedOptions?.find(
      (option) => option.name.trim().toLowerCase() === optionName
    );

    if (selected) {
      const mapped = this.#colorMap.get(selected.value.trim().toLowerCase());
      if (mapped) {
        this.#setColor(mapped);
        return;
      }
    }

    // No mapping for this value: fall back to whatever the swatch is painted
    // with, once the picker has finished re-rendering.
    event.promise?.then(() => this.#syncColorFromDom()).catch(() => {});
  };

  /**
   * @param {CustomEvent<{color?: string}>} event
   */
  #handleYarnColor = (event) => {
    const color = event.detail?.color;
    if (color) this.#setColor(color);
  };

  /**
   * @param {CustomEvent<{key?: string, node?: SVGElement | null}>} event
   */
  #handleIcon = (event) => {
    const { previewIcon } = this.refs;
    if (!previewIcon) return;

    const { key, node } = event.detail ?? {};
    // Cloning the rendered node keeps us from parsing any markup from a string.
    if (!node || !key || key === 'none') {
      previewIcon.replaceChildren();
      return;
    }

    previewIcon.replaceChildren(node.cloneNode(true));
  };

  /**
   * @param {string} value - One hex, or several separated by commas.
   * @returns {string[]}
   */
  #parseColors(value) {
    return value
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
  }

  /**
   * Reads the yarn color from the currently checked swatch, preferring the
   * dedicated picker and falling back to a variant option of the same name.
   */
  #syncColorFromDom() {
    // The picker may have announced its preselection before this element
    // connected, so read it straight from the DOM on startup.
    const picked = document.querySelector('.yarn-picker__input:checked');
    if (picked instanceof HTMLInputElement && picked.dataset.color) {
      this.#setColor(picked.dataset.color);
      return;
    }

    const optionName = this.#optionName;
    const inputs = document.querySelectorAll('variant-picker input[data-option-name]:checked');

    for (const input of inputs) {
      const name = input.getAttribute('data-option-name')?.trim().toLowerCase();
      if (name !== optionName) continue;

      const value = /** @type {HTMLInputElement} */ (input).value.trim().toLowerCase();
      const mapped = this.#colorMap.get(value);
      if (mapped) {
        this.#setColor(mapped);
        return;
      }

      const swatch = input.closest('label')?.querySelector('.swatch');
      const background = swatch instanceof HTMLElement ? swatch.style.getPropertyValue('--swatch-background') : '';

      // A photo swatch resolves to url(...), which is not a usable text color.
      if (background && !background.includes('url(')) {
        this.#setColor(background.trim());
      }
      return;
    }
  }

  /**
   * @param {string} color
   */
  #setColor(color) {
    this.#colors = this.#parseColors(color);
    // Single color paints via the CSS var; multicolor is painted per letter.
    this.#root?.style.setProperty('--ke-yarn-color', this.#colors[0] ?? color);
    this.#render();
  }

  /** @returns {string} The configured yarn color option name, lowercased. */
  get #optionName() {
    return this.#root?.getAttribute('data-color-option-name')?.trim().toLowerCase() ?? '';
  }

  #handleInvalid = () => {
    this.refs.textInput.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };
}

if (!customElements.get('name-embroidery-component')) {
  customElements.define('name-embroidery-component', NameEmbroidery);
}
